import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { Percent } from "lucide-react"
import { api } from "@/app/api/client"
import { KPICard } from "@/app/components/KPICard"
import { useClientTheme } from "@/app/components/ThemeContext"
import { LoadingState } from "@/app/components/LoadingState"
import { PageHeader } from "@/app/components/PageHeader"
import { ORRITable } from "./ORRITable"
import type { OrriEntry } from "./ORRIRow"

interface OrriResponse {
  data: OrriEntry[]
  meta: { total_count: number; page: number; per_page: number; total_pages: number }
}

export function ORRIPage() {
  const { theme } = useClientTheme()

  const { data, isLoading } = useQuery<OrriResponse>({
    queryKey: ["orri_inventories", "all"],
    queryFn: () => api.get<OrriResponse>("/orri_inventories?per_page=200"),
  })

  const rows = data?.data ?? []

  const avgOrri = useMemo(
    () => (rows.length ? rows.reduce((s, r) => s + Number(r.orri), 0) / rows.length : 0),
    [rows]
  )
  const uniqueTRs = useMemo(
    () => new Set(rows.map((r) => r.township_range).filter(Boolean)).size,
    [rows]
  )

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div>
        <PageHeader
          title="ORRI Entries"
          subtitle="Overriding royalty interests — all burdened acreage"
          theme={theme}
          icon={<Percent size={17} strokeWidth={1.75} />}
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-4">
          <KPICard
            label="Total Entries"
            value={rows.length}
            trend="ORRI records"
            accentColor={theme.kpiAccents[0]}
            icon={<Percent size={16} strokeWidth={1.5} />}
          />
          <KPICard
            label="Avg ORRI"
            value={`${(avgOrri * 100).toFixed(4)}%`}
            trend="Average across entries"
            accentColor={theme.kpiAccents[1]}
            icon={<Percent size={16} strokeWidth={1.5} />}
          />
          <KPICard
            label="Unique T-R Areas"
            value={uniqueTRs}
            trend="Township-range sections"
            accentColor={theme.kpiAccents[2]}
            icon={<Percent size={16} strokeWidth={1.5} />}
          />
        </div>

        <div className="mb-3">
          <h2
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "14px",
              fontWeight: 600,
              color: theme.textPrimary,
            }}
          >
            ORRI Register
          </h2>
          <p
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              color: theme.textMuted,
              marginTop: "1px",
            }}
          >
            All overriding royalty interest entries — burdened acreage &amp; rates
          </p>
        </div>

        {isLoading ? <LoadingState theme={theme} /> : <ORRITable data={rows} />}
      </div>
    </div>
  )
}
